import { useTranslation } from 'react-i18next';
import type { Wallet } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { CoinIcon } from '../CoinIcon';
import { CloseIcon } from '../icons';
import { Dialog } from './Dialog';

interface Props {
  wallet: Wallet;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Delete-wallet confirmation dialog — Figma node 2:7467: a centered card on a
 *  dimmed backdrop with the wallet identity and Delete / Cancel pill buttons. */
export function DeleteWalletDialog({ wallet, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  return (
    <Dialog onDismiss={onCancel}>
      {(close) => (
        <div
          className="relative flex w-[338px] flex-col items-center rounded-[36px] p-4 backdrop-blur-[2px]"
          style={{ backgroundColor: 'rgb(var(--surface) / 0.9)' }}
        >
          <button
            onClick={() => close(onCancel)}
            aria-label={t('common.close')}
            className="absolute right-4 top-4 z-10 grid size-9 place-items-center rounded-full text-ink active:scale-95"
            style={{ backgroundColor: 'rgb(var(--sheet) / 0.8)' }}
          >
            <CloseIcon width={20} height={20} />
          </button>
          <div className="relative flex w-full flex-col items-center gap-6">
            {/* title */}
            <div className="flex h-9 items-center justify-center">
              <p className="w-[276px] text-center text-[18px] font-medium leading-[1.32] text-ink">
                {t('wallet.deleteConfirm')}
              </p>
            </div>

            {/* wallet identity */}
            <div className="flex w-full flex-col items-center gap-4">
              <CoinIcon symbol="TRX" size={80} />
              <div className="flex h-[44px] w-full flex-col items-center justify-center gap-2">
                <p className="text-[24px] font-medium leading-none text-ink">{wallet.name}</p>
                <div className="flex items-center gap-[5px] text-[13px] leading-none text-muted">
                  {wallet.address.slice(0, 4)}····{wallet.address.slice(-4)}
                  <span className="inline-block size-[2px] rounded-full bg-muted" />
                  TRC-20
                </div>
              </div>
            </div>

            {/* actions */}
            <div className="flex w-full items-start gap-2">
              <button
                onClick={() => {
                  haptic('medium');
                  close(onConfirm);
                }}
                className="flex h-[52px] flex-[1_0_0] items-center justify-center rounded-full border border-card p-4 active:scale-[0.98]"
                style={{ backgroundColor: 'rgb(var(--press) / 0.08)' }}
              >
                <span className="text-[15px] font-bold leading-none text-negative">
                  {t('common.delete')}
                </span>
              </button>
              <button
                onClick={() => close(onCancel)}
                className="flex h-[52px] flex-[1_0_0] items-center justify-center rounded-full border border-card bg-accent p-4 active:scale-[0.98]"
              >
                <span className="text-[15px] font-bold leading-none text-white">
                  {t('common.cancel')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
